import { appSettings } from 'config/app.settings';
import { history } from 'utils';

export interface IDimeloIdentity {
  screenname: string;
  firstname: string;
  lastname: string;
  email?: string;
  uuid: string;
}

export interface IDimeloSignedIdentity extends IDimeloIdentity {
  exp: number;
}

export interface IDimeloIdentityExtraValues {
  [key: string]: string | number;
}

interface IConfiguration {
  embedded?: boolean;
  parentNodeId?: string;
}

let configuration: IConfiguration = {};

/**
 * Returns true if the supplied storage type is available. Used to circumvent iOS private browsing issues.
 * @param type is the type of storage to test for, either 'localStorage' or 'sessionStorage'
 */
const isStorageEnabled = (type: 'localStorage' | 'sessionStorage') => {
  try {
    const storage = window[type];
    const t = '__storage_test__';

    storage.setItem(t, t);
    storage.removeItem(t);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Maps to the window console only when the Dimelo DEBUG mode is enabled. Used for debugging
 * the integration of the Dimelo provider itself.
 */
export const dimeloLog =
  isStorageEnabled('localStorage') &&
  window.localStorage.getItem('dimelo:chat:debug:activated') === 'true' &&
  window.console
    ? window.console
    : {
        info: (...args: any[]) => ({ args }),
        table: (...args: any[]) => ({ args }),
      };

/**
 * Sets a custom variable value in the chat session. Before setting a custom variable, it is required
 * to first create it in Dimelo in the section 'Custom variables'.
 * @param name is the name of the custom value
 * @param value is the value to set
 */
export const setCustomVariable = (name: string, value: string): void => {
  dimeloLog.info('CHAT - setting custom variable', name, value);

  window._chatq = window._chatq || [];
  window._chatq.push(['_fillCustomVariable', `custom_${name}`, value]);
};

/**
 * Sets the identity of the chat consumer to the provider
 * @param values are predefined values to set as the identity
 * @param extraValues is an object with any additional values
 */
export const setIdentity = (values: IDimeloIdentity, extraValues: IDimeloIdentityExtraValues = {}): void => {
  dimeloLog.info('CHAT - setting identity', values, extraValues);

  window._chatq = window._chatq || [];
  window._chatq.push(['_setIdentity', { ...values, extra_values: extraValues }]);
};

const setSignedIdentity = (
  token: string,
  values: IDimeloIdentity,
  extraValues: IDimeloIdentityExtraValues = {}
): void => {
  dimeloLog.info('CHAT - setting signed identity', values, extraValues);

  window._chatq = window._chatq || [];
  window._chatq.push(['_setSignedIdentity', token]);

  window._chatq.push(
    ['_onEvent', 'started', (data: any) => console.log('CHAT STARTED: ', data)],
    ['_onEvent', 'chat_shown', (data: any) => console.log('CHAT SHOWN: ', data)],
    ['_onEvent', 'chat_closed', (data: any) => console.log('CHAT CLOSED: ', data)]
  );
};

const setConfiguration = (values: IConfiguration) => {
  configuration = { ...configuration, ...values };

  window._chatq = window._chatq || [];
  window._chatq.push(['_setConfiguration', configuration]);
};

/**
 * Updates the PAGE URL value in the current chat session
 * @param section is the optional new section to provide. If provided, it will update the 'section' custom
 * variable.
 */
export const setPageChange = (): void => {
  dimeloLog.info('CHAT - updating page');
  window._chatq = window._chatq || [];
  window._chatq.push(['_pageChange']);
};

/**
 * Loads the Dimelo chat tag
 */
export const setChatTag = () => {
  const d = document;
  const elementId = 'dimelo-chat-tag';

  // If the tag already exists in the DOM, then don't add it again
  if (d.getElementById(elementId)) {
    dimeloLog.info('CHAT - tag already loaded', d.getElementById(elementId));
    return;
  }

  const cm = d.createElement('script') as HTMLScriptElement;
  cm.type = 'text/javascript';
  cm.async = true;
  cm.src = `${d.location.protocol === 'https:' ? 'https://' : 'http://'}${appSettings.DIMELO_CHAT_ZIGGO_URL}`;
  cm.id = elementId;

  const s = d.getElementsByTagName('script')[0];
  s?.parentNode?.insertBefore(cm, s);
};

/**
 * Initializes the chat by loading the Dimelo loader and setting required custom variables
 * @param section is the section to add
 * @param customerType is the customertype to set
 */
export const useInitializeChat = (section: string, customerType?: string, parentNode?: string) => {
  setChatTag();

  if (parentNode) {
    setConfiguration({
      embedded: true,
      parentNodeId: parentNode,
    });
  }

  setCustomVariable('section', section);

  if (customerType) {
    setCustomVariable('customertype', customerType);
  }

  registerGtmEvents();
};

const registerGtmEvents = () => {
  const events = [
    'button_shown',
    'chat_engaged',
    'chat_shown',
    'chat_closed',
    'invitation_shown',
    'item_shown',
    'message_received',
    'message_sent',
  ];

  // Loop over the events and add a listener
  events.forEach((event) => {
    window._chatq = window._chatq || [];

    window._chatq.push([
      '_onEvent',
      event,
      (data: { mode: string; event: string; trigger_label: string }) => {
        let prev_events = [];

        if (window._ddm.get) {
          prev_events = window._ddm.get('page.chat_events') || [];
        }

        const action = data.mode ? `${data.event}|${data.mode}` : data.event;
        if (prev_events?.indexOf(action) > -1) {
          // if already tracked this action, don't do it again
          return;
        }
        prev_events.push(action);

        window._ddm.trigger(`chat.${data.event}`, {
          data: {
            ga_data: {
              category: 'chat.dimelo',
              action,
              label: `${data.trigger_label} | ${window.ga_id}`,
            },
          },
        });

        if (window._ddm.set) {
          window._ddm.set('page.chat_events', prev_events);
        }
      },
    ]);
  });
};

/**
 * Sets the identity of the user to Dimelo
 * @param user is the user which contains the identity info
 */
export const useSetChatIdentity = (values: IDimeloIdentity, extraValues?: { [key: string]: any }) => {
  setIdentity(values, {
    customer_id: values.uuid,
    ...extraValues,
  });
};

export const useSetSignedChatIdentity = (
  values: IDimeloIdentity,
  token: string,
  extraValues?: { [key: string]: any }
) => {
  setSignedIdentity(token, values, {
    customer_id: values.uuid,
    ...extraValues,
  });
};

/**
 * Notifies Dimelo whenever the url changes
 */
export const useSetChatPageUrl = () => {
  return history.listen(() => {
    setPageChange();
  });
};
